import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { Fragment, useRef, useState } from 'react';

import Box from 'shopper/components/Box';
import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Link from 'shopper/components/Link';
import Text from 'shopper/components/Text';

import Anchor from 'components/Anchor';
import Image from 'components/Image';

import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';
import { getArticleImageLink, toArticleImageAltText } from 'lib/image';
import { toArticleLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { noop } from 'lib/utils';

const MIN_ARTICLES_LENGTH = 3;

const BlogArticles = ({
  articles = [],
  gaEventCategory,
  headingClassName = null,
  onArticleClick = noop,
  ...rest
}) => {
  const [isImagesInViewport, setImagesInViewport] = useState(false);
  const blogArticlesRef = useRef(null);

  useIntersectionObserver({
    target: blogArticlesRef,
    threshold: 0,
    onIntersect: () => setImagesInViewport(true),
  });

  const onReadMoreClick = (e) => {
    sendEvent({ action: 'read_more_blog', category: gaEventCategory });
    onArticleClick(e);
  };

  if (articles.length < MIN_ARTICLES_LENGTH) {
    return null;
  }

  return (
    <div {...rest}>
      <Heading as="span" className={headingClassName} size="size4">
        {placeholder('titles.interestArticles')}
      </Heading>
      <div
        ref={blogArticlesRef}
        className="mt-4 grid grid-flow-row grid-cols-1 gap-4 md:mt-6 md:grid-cols-3"
      >
        {articles.map(
          ({
            articleId,
            articleDescription,
            articleImage,
            articleSlug,
            articleTitle,
          }) => (
            <Fragment key={articleId}>
              <div className="col-span-1">
                <Box className="flex flex-row p-4 md:flex-col">
                  {isImagesInViewport && (
                    <NextLink
                      href={toArticleLink(articleSlug)}
                      prefetch={false}
                      passHref
                    >
                      <Anchor className="h-20 w-[104px] md:h-[156px] md:w-full xl:w-[272px]">
                        <Image
                          alt={toArticleImageAltText(articleTitle)}
                          className="rounded-2 object-fill md:h-[156px] md:w-full xl:w-[272px]"
                          height={80}
                          src={getArticleImageLink(articleImage, 200)}
                          width={104}
                        />
                      </Anchor>
                    </NextLink>
                  )}
                  <div className="ml-2 flex flex-col justify-between md:ml-0 md:h-[220px]">
                    <div>
                      <Text
                        className="line-clamp-3 md:my-4 md:line-clamp-2"
                        bold
                      >
                        {articleTitle}
                      </Text>
                      <Text
                        className="md:line-clamp-8 hidden md:block lg:line-clamp-5"
                        size="size2"
                      >
                        {articleDescription}
                      </Text>
                    </div>
                    <NextLink
                      href={toArticleLink(articleSlug)}
                      prefetch={false}
                      passHref
                    >
                      <Anchor className="w-fit">
                        <Link onClick={onReadMoreClick}>
                          {placeholder('actions.seeMoreArticles')}
                          <Icon className="ml-2" name="arrow-right-narrow" />
                        </Link>
                      </Anchor>
                    </NextLink>
                  </div>
                </Box>
              </div>
            </Fragment>
          )
        )}
      </div>
    </div>
  );
};

BlogArticles.propTypes = {
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      articleId: PropTypes.number.isRequired,
      articleDescription: PropTypes.string.isRequired,
      articleSlug: PropTypes.string.isRequired,
      articleTitle: PropTypes.string.isRequired,
    })
  ),
  gaEventCategory: PropTypes.string.isRequired,
  headingClassName: PropTypes.string,
  onArticleClick: PropTypes.func,
};

export default BlogArticles;
